import { useMe } from "@/api/services/admin/auth";
import { deleteAdminUserApi, useAdminUser } from "@/api/services/admin/user";
import { IconDelete } from "@/assets/icons";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";
import { FooterContainer } from "@/components/commons/FooterContainer";
import { Button } from "@/components/elements/Button";
import { InnerBox } from "@/components/elements/InnerBox";
import { Select } from "@/components/elements/Select";
import { LoadingPage } from "@/components/elements/Loading/Loading";
import { MenuNavigate } from "@/components/elements/MenuNavigate";
import { TopPage } from "@/components/elements/TopPage";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { AdminUserForm } from "@/features/admin-user/components/AdminUserForm";
import { convertUserDetailToForm } from "@/features/admin-user/utils/converts";
import { useDialog } from "@/hooks/useDialog";
import { useToast } from "@/hooks/useToast";
import { AppRoutes } from "@/routes/config";
import { showError } from "@/utils/error";
import { isNumber } from "@/utils/number";
import { getDynamicRoute } from "@/utils/url";
import { text } from "@/theme/colors";
import { typography } from "@/theme/typography";
import { Box, FormControl, FormLabel, Grid, Stack, css, styled } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

export const AdminUserDetailPage: FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: adminUserResult, isLoading, isError } = useAdminUser();
  const confirmDelete = useDialog();
  const adminUserData = adminUserResult?.data;
  const queryClient = useQueryClient();
  const toast = useToast();
  const { data: meResult } = useMe();
  const meData = meResult?.data;

  useEffect(() => {
    if (!id || !isNumber(id)) {
      navigate(AppRoutes.adminUser, { replace: true });
    }
  }, [id, navigate]);

  useEffect(() => {
    if (id && isNumber(id) && !isLoading && (isError || !adminUserData)) {
      navigate(AppRoutes.adminUser, { replace: true });
    }
  }, [id, isLoading, isError, adminUserData, navigate]);

  const handleDelete = async () => {
    try {
      if (!adminUserData?.id) throw new Error();
      const agreeToDelete = await confirmDelete.show();
      if (!agreeToDelete) return;
      const rs = await deleteAdminUserApi(adminUserData.id);
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      navigate(AppRoutes.adminUser);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ADMIN_USER.FETCH_LIST] });
    } catch (error) {
      showError(error);
    }
  };

  if (isLoading || !adminUserData) return <LoadingPage />;

  return (
    <AdminUserDetailWrapper sx={{ mt: 1 }}>
      <Box sx={{ mb: 2.5 }}>
        <TopPage
          title="irohana担当者の詳細"
          description="顧客側画面にログインできるユーザーアカウントに関する情報を編集できます。"
          enablePageBack={false}
          rightContent={<Stack direction="row" alignItems="center" gap={3}></Stack>}
          backUrl={AppRoutes.adminUser}
        />
        <Box mt="36px">
          <MenuNavigate
            contentWidth="900px"
            contents={[
              {
                title: "ユーザーの状態",
                content: (
                  <InnerBox title="ユーザーの状態" padding={3}>
                    <Grid container columnSpacing={3} rowSpacing={2}>
                      <Grid item xs={6}>
                        <StyledFormControl fullWidth>
                          <FormLabel required>ステータス</FormLabel>
                          <Select value={adminUserData?.activeStatus} enumName="EUserActiveStatus" readonly nonControl />
                        </StyledFormControl>
                      </Grid>
                    </Grid>
                  </InnerBox>
                ),
              },
              {
                title: "基本情報",
                content: (
                  <InnerBox title="基本情報" padding={3}>
                    <AdminUserForm previousData={convertUserDetailToForm(adminUserData)} readonly />
                  </InnerBox>
                ),
              },
            ]}
            contentAffix={
              <FooterContainer sx={{ mt: 4 }}>
                <Stack gap={1} direction="row" justifyContent="space-between" alignItems="center">
                  <Button size="sm-round" variant="secondary" component={Link} to={AppRoutes.adminUser}>
                    戻る
                  </Button>
                  <Stack direction="row" gap={3}>
                    <Button variant="danger" disabled={adminUserData?.id === meData?.id} onClick={handleDelete} startIcon={<IconDelete />}>
                      削除
                    </Button>
                    <Button component={Link} variant="primary" size="sm-round" to={getDynamicRoute(AppRoutes.adminUserEdit, { id })}>
                      編集
                    </Button>
                  </Stack>
                </Stack>
              </FooterContainer>
            }
          />
        </Box>
        <ConfirmDeleteModal
          open={confirmDelete.open}
          onClose={confirmDelete.cancel}
          onOk={confirmDelete.confirm}
          message="irohana担当者を削除してもよろしいですか？"
        />
      </Box>
    </AdminUserDetailWrapper>
  );
};

const AdminUserDetailWrapper = styled(Stack)`
  width: 1198px;
  margin: 0 auto;
`;

const StyledFormControl = styled(FormControl)`
  .MuiFormLabel-root {
    color: ${text.primary};
    margin-bottom: 4px;
    ${css(typography.cap12)};
  }
  .MuiFormLabel-asterisk {
    color: ${text.error};
  }
`;
